import {
    PropertyName,
    VariableReference,
    Variable,
    VariableWithPropertyName,
    VariableValue,
    VariableCollection,
    VariableValueType,
    VariableMode
} from "../api/model/variables/Variables";

import BasicRecord from "./BasicRecord";

function getVariableReferenceByPropertyName(variableReferences: VariableReference[] | undefined,
    propertyName: PropertyName) : VariableReference | undefined {
    if (!variableReferences) {
        return undefined;
    }

    return variableReferences.find(variable => variable.propertyName === propertyName);
}

function getVariableByPropertyName(variableReferences: VariableReference[] | undefined,
    propertyName: PropertyName,
    mappedVariables: BasicRecord<Variable> | undefined) : VariableWithPropertyName | undefined {
    if (!variableReferences || !mappedVariables) {
        return undefined;
    }

    const variableReference = getVariableReferenceByPropertyName(variableReferences, propertyName);
    if (!variableReference) {
        return undefined;
    }

    const variable = mappedVariables[variableReference.variableSourceId] ||
        Object.values(mappedVariables).find(value => value.variableId === variableReference.variableSourceId);

    if (!variable) {
        return undefined;
    }

    return { ...variable, propertyName };
}

function getReferencedVariableData(
    sourceIdVariableMap: BasicRecord<Variable>,
    { value, variable }: { value: VariableValue; variable?: Variable; },
    visited = new Set<string>()
): {
    value: VariableValue;
    variable: Variable;
} | null {
    if (value.type === "variable") {
        if (visited.has(value.variableSourceId!)) {
            // Prevents circular references between variables that could cause an infinite loop
            return null;
        }

        visited.add(value.variableSourceId!);

        const referencedVariable = sourceIdVariableMap[value.variableSourceId!];

        if (referencedVariable) {
            const referencedVariableValue = referencedVariable.values
                .find(item => item.modeId === value.modeId) || referencedVariable.values[0];

            if (referencedVariableValue.variableSourceId === value.variableSourceId &&
                referencedVariableValue.modeId === value.modeId
            ) { // to break self referencing variables
                return null;
            }

            return getReferencedVariableData(sourceIdVariableMap, {
                value: referencedVariableValue, variable: referencedVariable
            }, visited);
        }

        return null;
    }

    return {
        value,
        variable: variable!
    };
}

function getVariableModeIdMap(variableCollections: VariableCollection[]): BasicRecord<VariableMode> {
    return variableCollections
        .flatMap(({ modes }) => modes)
        .reduce((acc, mode): BasicRecord<VariableMode> => {
            acc[mode._id] = mode;

            return acc;
        }, {} as BasicRecord<VariableMode>);
}

function getSourceIdVariableMap(variableCollections: VariableCollection[]): BasicRecord<Variable> {
    return variableCollections
        .flatMap(({ groups }) => groups
            .flatMap(({ variables }) => variables
                .flatMap(variable => variable)))
        .reduce((acc, variable): BasicRecord<Variable> => {
            acc[variable.sourceId] = variable;

            return acc;
        }, {} as BasicRecord<Variable>);
}

function filterVariableCollectionsByValueType({
    variableCollections,
    valueType,
    sourceIdVariableMap
}: {
    variableCollections: VariableCollection[];
    valueType: VariableValueType;
    sourceIdVariableMap: BasicRecord<Variable>;
}): VariableCollection[] {
    return variableCollections.map(variableCollection => {
        const computedGroups = variableCollection.groups.map((group, index) => ({
            ...group,
            isDefault: !index,
            variables: group.variables.map(variable => {
                const computedValues = variable.values.map(value => {
                    if (value.type === "variable") {
                        return {
                            ...value,
                            reference: getReferencedVariableData(sourceIdVariableMap, { value })
                        };
                    }

                    return value;
                });
                const hasUnavailableReference = computedValues.some(computedValue => computedValue.type === "variable" && !computedValue.reference);

                return {
                    ...variable,
                    values: hasUnavailableReference
                        ? []
                        : computedValues
                            .filter(({ type, reference }) => [type, reference?.value.type].includes(valueType))
                };
            }).filter(({ values }) => values.length)
        }));

        return {
            ...variableCollection,
            groups: computedGroups
                .filter(({ _id, name, isDefault, variables }) => (
                    variables.length ||
                    isDefault ||
                    computedGroups.some(item =>
                        _id !== item._id &&
                        isChildGroup(name, item.name) &&
                        item.variables.length)
                ))
        };
    }).filter(({ groups }) => (groups.length === 1 ? groups[0].variables.length : groups.length));
}

function isChildGroup(parentName: string, childName: string) {
    return childName === parentName || childName.startsWith(`${parentName}/`);
}

export {
    getVariableByPropertyName,
    getVariableModeIdMap,
    getSourceIdVariableMap,
    filterVariableCollectionsByValueType,
    isChildGroup
};
